/**
 * "Ask the owner": a member without billing access sends a top-up, plan-change or hire request to the workspace's
 * owner through the standing notification channels (task #77, 2026-08-18). ONE copy, read by /subscription, today's
 * /pricing and the pricing page's rebuild in the new design.
 *
 * The server verifies membership, rate-limits the request and discloses no billing data to the person asking
 * (functions/src/rbac/functions/billingRequests.ts). The callable's client is loaded when a request is sent, never at
 * this module's load.
 */
import { useCallback, useState } from 'react';

export type BillingRequestKind = 'top_up' | 'plan_change' | 'hire';
export type BillingRequestState = 'idle' | 'sending' | 'sent' | 'failed';

export type BillingRequestCall = (payload: { accountId: string; kind: BillingRequestKind }) => Promise<unknown>;

async function callRequestBillingAttention(payload: { accountId: string; kind: BillingRequestKind }): Promise<unknown> {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const { getApp } = await import('firebase/app');
  const fn = httpsCallable(getFunctions(getApp()), 'requestBillingAttention');
  return fn(payload);
}

export function useBillingAttentionRequest(call: BillingRequestCall = callRequestBillingAttention) {
  const [state, setState] = useState<BillingRequestState>('idle');
  const send = useCallback(async (accountId: string | undefined, kind: BillingRequestKind) => {
    if (!accountId) return;
    setState('sending');
    try {
      await call({ accountId, kind });
      setState('sent');
    } catch {
      setState('failed');
    }
  }, [call]);
  return { state, send };
}
